import { useMutation, useQuery } from "@apollo/client";

import type {
  CreateCustomViewInput,
  CustomView,
  CustomViewFilters,
  CustomViewLayout,
  CustomViewListPayload,
  CustomViewModule,
  CustomViewModuleMeta,
  SaveCustomViewAsNewInput,
  UpdateCustomViewInput,
  UserViewPreference,
} from "~/features/CustomViews/types";

import { CUSTOM_VIEW_FIELDS } from "./fragments";
import {
  CREATE_CUSTOM_VIEW,
  CREATE_CUSTOM_VIEW_MUTATION,
  DELETE_CUSTOM_VIEW,
  DELETE_CUSTOM_VIEW_MUTATION,
  REORDER_CUSTOM_VIEWS,
  REORDER_CUSTOM_VIEWS_MUTATION,
  SAVE_CUSTOM_VIEW_AS_NEW,
  SAVE_CUSTOM_VIEW_AS_NEW_MUTATION,
  SET_ACTIVE_CUSTOM_VIEW,
  SET_ACTIVE_CUSTOM_VIEW_MUTATION,
  UPDATE_CUSTOM_VIEW,
  UPDATE_CUSTOM_VIEW_MUTATION,
} from "./mutations";
import {
  GET_CUSTOM_VIEW_MODULE_META,
  GET_CUSTOM_VIEW_MODULE_META_QUERY,
  LIST_CUSTOM_VIEWS,
  LIST_CUSTOM_VIEWS_QUERY,
} from "./queries";

/** Strip Apollo `__typename` — GraphQL input types reject it. */
const stripTypename = <T extends Record<string, unknown>>(obj: T): T => {
  const { __typename: _t, ...rest } = obj as T & { __typename?: string };
  return rest as T;
};

const toLayoutInput = (
  layout: CustomViewLayout | null | undefined,
): CustomViewLayout | null | undefined => {
  if (layout == null) return layout;
  const clean = stripTypename(layout as Record<string, unknown>);
  return {
    boardGroupBy: (clean.boardGroupBy as string | null | undefined) ?? null,
    columns: Array.isArray(clean.columns) ? (clean.columns as string[]) : [],
  };
};

const toFiltersInput = (
  filters: CustomViewFilters | null | undefined,
): CustomViewFilters | null | undefined => {
  if (filters == null) return filters;
  const clean = stripTypename(filters as Record<string, unknown>);
  const rawActive = (clean.active as Record<string, unknown>) || {};
  // Drop empty values so Clear all sends a real empty `active: {}` replace.
  const active: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(rawActive)) {
    if (Array.isArray(value)) {
      if (value.length === 2 && value.every((v) => v === "" || v == null)) {
        continue;
      }
      if (value.length > 0) active[key] = value;
      continue;
    }
    if (value && typeof value === "object" && "value" in value) {
      const inner = (value as { value?: unknown }).value;
      const ok = Array.isArray(inner)
        ? inner.length > 0
        : inner != null && inner !== "";
      if (ok) active[key] = value;
      continue;
    }
    if (typeof value === "boolean" || (value != null && value !== "")) {
      active[key] = value;
    }
  }
  return {
    selectedIds: Array.isArray(clean.selectedIds)
      ? (clean.selectedIds as string[]).filter(Boolean)
      : [],
    active,
  };
};

/** Map FE `type` → BE `viewType` for mutation inputs. */
const toCreateInput = (input: CreateCustomViewInput) => ({
  module: input.module,
  viewType: input.type,
  visibility: input.visibility,
  ...(input.name ? { name: input.name } : {}),
});

const toUpdateInput = (input: UpdateCustomViewInput) => {
  const { type, filters, layout, sort, ...rest } = input;
  return {
    ...rest,
    ...(type !== undefined ? { viewType: type } : {}),
    ...(filters !== undefined ? { filters: toFiltersInput(filters) } : {}),
    ...(layout !== undefined ? { layout: toLayoutInput(layout) } : {}),
    // Explicit so clearing sort (`null` / `[]`) is always sent — not dropped.
    ...(sort !== undefined ? { sort: sort ?? [] } : {}),
  };
};

const toSaveAsNewInput = (input: SaveCustomViewAsNewInput) => ({
  sourceViewId: input.sourceViewId,
  name: input.name,
  ...(input.filters !== undefined
    ? { filters: toFiltersInput(input.filters) }
    : {}),
  ...(input.sort !== undefined ? { sort: input.sort } : {}),
  ...(input.layout !== undefined
    ? { layout: toLayoutInput(input.layout) }
    : {}),
});

export const useListCustomViews = (module: CustomViewModule, skip = false) => {
  const { data, loading, error, refetch } = useQuery(LIST_CUSTOM_VIEWS_QUERY, {
    variables: { module },
    skip,
    fetchPolicy: "cache-and-network",
    errorPolicy: "all",
  });

  if (error && process.env.NODE_ENV !== "production") {
    // eslint-disable-next-line no-console
    console.error("[listCustomViews]", module, error.message);
  }

  const payload: CustomViewListPayload | null =
    data?.[LIST_CUSTOM_VIEWS] ?? null;

  return {
    views: (payload?.views ?? []) as CustomView[],
    preference: (payload?.preference ?? null) as UserViewPreference | null,
    tabLimit: payload?.tabLimit ?? 5,
    nameMaxLength: payload?.nameMaxLength ?? 15,
    canCreateShared: Boolean(payload?.canCreateShared),
    loading,
    error,
    refetch,
  };
};

export const useCustomViewModuleMeta = (
  module: CustomViewModule,
  skip = false,
) => {
  const { data, loading, error } = useQuery(GET_CUSTOM_VIEW_MODULE_META_QUERY, {
    variables: { module },
    skip,
    fetchPolicy: "cache-first",
  });

  return {
    meta: (data?.[GET_CUSTOM_VIEW_MODULE_META] ??
      null) as CustomViewModuleMeta | null,
    loading,
    error,
  };
};

export const useCreateCustomView = () => {
  const [mutate, { loading, error }] = useMutation(CREATE_CUSTOM_VIEW_MUTATION);

  const createCustomView = async (input: CreateCustomViewInput) => {
    const result = await mutate({
      variables: { input: toCreateInput(input) },
      refetchQueries: [
        { query: LIST_CUSTOM_VIEWS_QUERY, variables: { module: input.module } },
      ],
    });
    return result.data?.[CREATE_CUSTOM_VIEW] as CustomView;
  };

  return { createCustomView, loading, error };
};

export const useUpdateCustomView = (module?: CustomViewModule) => {
  const [mutate, { loading, error }] = useMutation(UPDATE_CUSTOM_VIEW_MUTATION);

  const updateCustomView = async (
    input: UpdateCustomViewInput,
    options?: { skipRefetch?: boolean },
  ) => {
    const result = await mutate({
      variables: { input: toUpdateInput(input) },
      // Keep list cache in sync — otherwise remount hydrates stale empty filters.
      // skipRefetch: filter/sort autosave — full list refetch caused UI loops, so
      // write the mutation result into the normalized CustomView instead.
      ...(module && !options?.skipRefetch
        ? {
            refetchQueries: [
              {
                query: LIST_CUSTOM_VIEWS_QUERY,
                variables: { module },
              },
            ],
            awaitRefetchQueries: true,
          }
        : {
            update(cache, { data }) {
              const updated = data?.[UPDATE_CUSTOM_VIEW] as
                | CustomView
                | undefined;
              if (!updated?._id) return;
              cache.writeFragment({
                id: cache.identify({
                  __typename: "CustomView",
                  _id: updated._id,
                }),
                fragment: CUSTOM_VIEW_FIELDS,
                fragmentName: "CustomViewFields",
                data: updated,
              });
            },
          }),
    });
    return result.data?.[UPDATE_CUSTOM_VIEW] as CustomView;
  };

  return { updateCustomView, loading, error };
};

export const useDeleteCustomView = () => {
  const [mutate, { loading, error }] = useMutation(DELETE_CUSTOM_VIEW_MUTATION);

  const deleteCustomView = async (id: string, module: CustomViewModule) => {
    const result = await mutate({
      variables: { id },
      refetchQueries: [
        { query: LIST_CUSTOM_VIEWS_QUERY, variables: { module } },
      ],
    });
    return result.data?.[DELETE_CUSTOM_VIEW];
  };

  return { deleteCustomView, loading, error };
};

export const useSaveCustomViewAsNew = () => {
  const [mutate, { loading, error }] = useMutation(
    SAVE_CUSTOM_VIEW_AS_NEW_MUTATION,
  );

  const saveCustomViewAsNew = async (input: SaveCustomViewAsNewInput) => {
    const result = await mutate({
      variables: { input: toSaveAsNewInput(input) },
    });
    return result.data?.[SAVE_CUSTOM_VIEW_AS_NEW] as CustomView;
  };

  return { saveCustomViewAsNew, loading, error };
};

export const useReorderCustomViews = () => {
  const [mutate, { loading, error }] = useMutation(
    REORDER_CUSTOM_VIEWS_MUTATION,
  );

  const reorderCustomViews = async (
    module: CustomViewModule,
    viewOrder: string[],
  ) => {
    const result = await mutate({
      variables: { input: { module, viewIds: viewOrder } },
      refetchQueries: [
        { query: LIST_CUSTOM_VIEWS_QUERY, variables: { module } },
      ],
    });
    return result.data?.[REORDER_CUSTOM_VIEWS] as CustomView[];
  };

  return { reorderCustomViews, loading, error };
};

export const useSetActiveCustomView = () => {
  const [mutate, { loading, error }] = useMutation(
    SET_ACTIVE_CUSTOM_VIEW_MUTATION,
  );

  const setActiveCustomView = async (
    module: CustomViewModule,
    viewId: string,
  ) => {
    const result = await mutate({
      variables: { module, viewId },
      // Keep listCustomViews preference in sync so leaving/returning the page
      // restores the tab the user actually selected (not a stale cache hit).
      update(cache, { data }) {
        const nextPref = data?.[SET_ACTIVE_CUSTOM_VIEW] as
          | UserViewPreference
          | undefined;
        if (!nextPref) return;
        cache.updateQuery(
          { query: LIST_CUSTOM_VIEWS_QUERY, variables: { module } },
          (existing: { [key: string]: CustomViewListPayload } | null) => {
            const payload = existing?.[LIST_CUSTOM_VIEWS];
            if (!payload) return existing;
            return {
              ...existing,
              [LIST_CUSTOM_VIEWS]: {
                ...payload,
                preference: {
                  ...(payload.preference ?? {}),
                  ...nextPref,
                },
              },
            };
          },
        );
      },
    });
    return result.data?.[SET_ACTIVE_CUSTOM_VIEW] as UserViewPreference;
  };

  return { setActiveCustomView, loading, error };
};
